import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

import Button from '../Button/Button';

import classNames from '../../utils/classNames';
import styles from './popup.scss';

export default function Popup(props) {
    const {
        autoclose,
        title,
        description,
        style,
        maxWidth,
        onConfirm,
        onClosed,
        canCloseOutside,
        loading,
        noConfirm,
        cancelText,
        confirmText,
        confirmDanger,
        children,
    } = props;
    const wrapperEl = React.useRef(null);
    const [isShown, setIsShown] = React.useState(true);
    const [closed, setClosed] = React.useState(false);

    function handleWrapperClick(e) {
        if (wrapperEl?.current === e.target && canCloseOutside) {
            closePopup();
        }
    }

    function handleConfirm() {
        if (typeof onConfirm === 'function') {
            onConfirm();
        }

        if (autoclose) closePopup();
    }

    function handleCancelClick(e) {
        e.stopPropagation();
        closePopup();
    }

    function handleAnimationEnd() {
        if (closed) {
            setIsShown(false);

            if (typeof onClosed === 'function') onClosed();
        }
    }

    function closePopup() {
        setClosed(true);
    }

    /**
     * Check if we should close popup.
     */
    React.useEffect(() => {
        if (props.close) {
            closePopup();
        }
    }, [props.close]);

    return (
        isShown && ReactDOM.createPortal(
            <div className={classNames(styles.wrapper, {
                [styles.closed]: closed,
            })}
            style={style}
            onAnimationEnd={handleAnimationEnd}
            onClick={handleWrapperClick}
            >
                <div className={classNames(styles.container, {
                    [styles.closed]: closed,
                    'panel-in': true,
                })} style={{ maxWidth }}>
                    <div className={styles.header}>
                        <h2>{title}</h2>
                        {typeof description === 'string' && <p className={styles.description}>
                            {description}
                        </p>}
                    </div>
                    {children !== undefined && <div className={styles.content}>
                        {children}
                    </div>}
                    <div className={styles.footer}>
                        <Button level={'tertiary'} text={cancelText || 'Отмена'} onClick={handleCancelClick} />
                        {!noConfirm && <Button level={'tertiary'} danger={confirmDanger} loading={loading} text={confirmText || 'Продолжить'} onClick={handleConfirm} />}
                    </div>
                </div>
                <div className={styles.overlay} ref={wrapperEl}></div>
            </div>,
            document.getElementById('popouts-portal')
        )
    );
};

Popup.propTypes = {
    /**
     * Close popup right after Confirm pressed.
     */
    autoclose: PropTypes.bool,
    /**
     * If true, then popup will be closed.
     */
    close: PropTypes.bool,
    /**
     * Popup title.
     */
    title: PropTypes.string.isRequired,
    /**
     * Popup description text.
     */
    description: PropTypes.string,
    /**
     * Popup custom content.
     */
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    /**
     * Popup container max width.
     */
    maxWidth: PropTypes.number,
    /**
     * On Confirm button pressed.
     */
    onConfirm: PropTypes.func,
    /**
     * Callback to trigger on popup closed.
     */
    onClosed: PropTypes.func,
    /**
     * Allow users to close popup by clicking outside it's content.
     */
    canCloseOutside: PropTypes.bool,
    /**
     * Removes Confirm button.
     */
    noConfirm: PropTypes.bool,
    /**
     * Text to display on Cancel button.
     */
    cancelText: PropTypes.string,
    /**
     * Text to display on Confirm button.
     */
    confirmText: PropTypes.string,
    /**
     * Is confirmation is a danger action?
     */
    confirmDanger: PropTypes.bool,
    /**
     * Display loader on the confirmation button.
     */
    loading: PropTypes.bool,
};
